import {
    React,
    Box,
    Grid2,
    useState, useParams, useRef, useApi, useOutletContext,
    CustomButton, CustomForecast, CustomBackdrop, ProjectHistoric, ProjectInvoice, ForecastDisplayType,
    Toaster, FormType,
    Typography,
    OpenInFullIcon, CloseFullscreenIcon, IconButton,
    useEffect
} from '../../../utils/globalImports';
import { usePageLock } from '../../../hooks/usePageLock';

import { ProjectForecast, Historic } from '../../../interfaces/forecast.interface';

interface CustomForecastRef {
    triggerSave: () => void;
}

const ForecastTracker: React.FC = () => {
    const { execute, loading } = useApi();
    const [projectData] = useOutletContext<any>();
    const [forecastType, setForecastType] = useState<number>();

    const forecastRef = useRef<CustomForecastRef>(null);

    const { projectId } = useParams<{ projectId?: string }>(); // Get projectId from URL
    const [forecastData, setforecastData] = useState<ProjectForecast[] | null>([]);
    const [historicData, setHistoricData] = useState<Historic[] | null>([]);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [modal, setModal] = useState(false);
    const userName = localStorage.getItem("name");
    const [isLocked, setIslocked] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isBaselineExists, setIsBaselineExists] = useState(false);
    const userId = Number(localStorage.getItem("userId"));
    const { requestLock, requestUnlock, lockedBy } = usePageLock(
        FormType.Project,
        Number(projectId),
        userName!
    );
    const checkBaselineExists = async () => {
        try {
            const response = await execute<{ exists: boolean }>(
                "get",
                `/ProjectBaseline/CheckBaselineExists/${userId}/${projectId}`
            );

            if (response?.data) {
                setIsBaselineExists(response.data.exists);
            } else {
                setIsBaselineExists(false);
            }
        } catch (error) {
            console.error("Error checking baseline:", error);
        }
    };

    useEffect(() => {
        if (projectId) {
            checkBaselineExists();
        }
    }, [projectId]);
    const fetchForecastData = async (forecastType: number) => {
        try {
            const formType =
                forecastType === ForecastDisplayType.LiveDefinite
                    ? FormType.LiveDefinite
                    : forecastType === ForecastDisplayType.LiveProposed
                        ? FormType.LiveProposed
                        : forecastType === ForecastDisplayType.ThirdPartyCost
                            ? FormType.ThirdPartyCost
                            : null;

            if (!formType) {
                setToast({ open: true, message: "Invalid forecast type", type: "error" });
                return;
            }

            const res = await requestLock(formType, Number(projectId), userName!);
            if (res.success) {
                setIslocked(true);

            } else {
                setToast({ open: true, message: res.message, type: "warning" });
                setIslocked(false);

            }

            const { data } = await execute<ProjectForecast[]>(
                'get',
                `/Project/GetForecastHierarchy/${projectId}/${forecastType}`
            );
            setforecastData(data);
            if (data)
                setForecastType(forecastType);
            else
                setToast({ open: true, message: "No data available", type: "warning" });
        } catch (err) {
            console.error("Lock request failed", err);
        }
    };


    const fetchHistoricData = async (forecastType: number, oldforecastType: number) => {
        if (isLocked) {
            const formType =
                oldforecastType === ForecastDisplayType.LiveDefinite
                    ? FormType.LiveDefinite
                    : oldforecastType === ForecastDisplayType.LiveProposed
                        ? FormType.LiveProposed
                        : oldforecastType === ForecastDisplayType.ThirdPartyCost
                            ? FormType.ThirdPartyCost
                            : null;

            if (formType) {
                await requestUnlock(formType, Number(projectId));
            }
        }
        const { data } = await execute<Historic[]>('get', `/Project/GetHistoricData/${projectId}/${forecastType}`);
        setHistoricData(data);
        setForecastType(forecastType);
        if (!data)
            setToast({ open: true, message: "No data available", type: "warning" });

        setIslocked(false);
    }

    return (
        <Box>
            <Box
                sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    // width: isFullscreen ? "calc(100% - 250px)" : "100%",
                    width: isFullscreen ? "100%" : "100%",
                    height: isFullscreen ? "100vh" : "calc(100vh - 250px)",
                    position: isFullscreen ? "fixed" : "relative",
                    top: isFullscreen ? 0 : "auto",
                    // left: isFullscreen ? "250px" : "auto",
                    left: isFullscreen ? 0 : "auto",
                    right: 0,
                    bottom: 0,
                    zIndex: isFullscreen ? 1200 : "auto",
                    background: "white",
                    //overflow:forecastType? "auto":"hidden", // ✅ allow scrolling
                }}
            >

                <Grid2 container spacing={6} sx={{ display: 'flex', m: 2 }}>
                    <Grid2 size={{ xs: 12, md: 12, lg: 2 }}>
                        <CustomButton
                            disabled={isBaselineExists}
                            sx={{
                                mt: 0,
                                mb: 2,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                color: forecastType === ForecastDisplayType.LiveDefinite ? '#fff' : '#1e66ab',
                            }}
                            backgroundColor={forecastType === ForecastDisplayType.LiveDefinite ? '#1e66ab' : '#f0f0f0'}
                            hoverColor="#f0f0f0"
                            hoverFontColor="#1e66ab"
                            onClick={async () => {
                                await fetchForecastData(ForecastDisplayType.LiveDefinite);
                            }}
                        >
                            Live Definite
                        </CustomButton>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 12, lg: 2 }}>
                        <CustomButton
                            disabled={isBaselineExists}
                            sx={{
                                mt: 0,
                                mb: 2,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                color: forecastType === ForecastDisplayType.LiveProposed ? '#fff' : '#1e66ab',
                            }}
                            backgroundColor={forecastType === ForecastDisplayType.LiveProposed ? '#1e66ab' : '#f0f0f0'}
                            hoverColor="#f0f0f0"
                            hoverFontColor="#1e66ab"
                            onClick={async () => {
                                await fetchForecastData(ForecastDisplayType.LiveProposed);
                            }}
                        >
                            Live Proposed
                        </CustomButton>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 12, lg: 2 }}>
                        <CustomButton
                            disabled={isBaselineExists}
                            sx={{
                                mt: 0,
                                mb: 2,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                color: forecastType === ForecastDisplayType.ThirdPartyCost ? '#fff' : '#1e66ab',
                            }}
                            backgroundColor={forecastType === ForecastDisplayType.ThirdPartyCost ? '#1e66ab' : '#f0f0f0'}
                            hoverColor="#f0f0f0"
                            hoverFontColor="#1e66ab"
                            onClick={async () => {
                                await fetchForecastData(ForecastDisplayType.ThirdPartyCost);
                            }}
                        >
                            Third Party Cost
                        </CustomButton>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 12, lg: 2 }}>
                        <CustomButton
                            sx={{
                                mt: 0,
                                mb: 2,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                color: forecastType === ForecastDisplayType.Historic ? '#fff' : '#1e66ab',
                            }}
                            backgroundColor={forecastType === ForecastDisplayType.Historic ? '#1e66ab' : '#f0f0f0'}
                            hoverColor="#f0f0f0"
                            hoverFontColor="#1e66ab"
                            onClick={async () => {
                                await fetchHistoricData(ForecastDisplayType.Historic, Number(forecastType));
                            }}
                        >
                            Historic
                        </CustomButton>
                    </Grid2>

                    {(forecastType === ForecastDisplayType.LiveDefinite ||
                        forecastType === ForecastDisplayType.ThirdPartyCost) && (
                            <Grid2 size={{ xs: 12, md: 12, lg: 2 }}>
                                <CustomButton
                                    sx={{
                                        mt: 0,
                                        mb: 2,
                                        textTransform: 'none',
                                        whiteSpace: 'nowrap',
                                        color: '#1e66ab',
                                    }}
                                    backgroundColor={'#f0f0f0'}
                                    hoverColor="#f0f0f0"
                                    hoverFontColor="#1e66ab"
                                    onClick={async () => {
                                        setModal(true);
                                    }}
                                >
                                    Create Invoice
                                </CustomButton>
                            </Grid2>
                        )}


                    <Grid2 size={{ xs: 12, md: 12, lg: 1.5, }} sx={{ ml: 'auto' }}>
                        <CustomButton
                            sx={{
                                mt: 0,
                                mb: 2,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                            }}
                            type='button'
                            disabled={!forecastData?.length || !isLocked || forecastType === ForecastDisplayType.Historic}
                            onClick={async () => {
                                const success = await forecastRef.current?.triggerSave();
                                if (success) {
                                    await fetchForecastData(forecastType!);
                                }
                            }}
                        >
                            Save
                        </CustomButton>

                        {(
                            forecastType === ForecastDisplayType.LiveDefinite ||
                            forecastType === ForecastDisplayType.LiveProposed ||
                            forecastType === ForecastDisplayType.ThirdPartyCost
                        ) && lockedBy && lockedBy !== userName && (
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "red", whiteSpace: "nowrap" }}
                                    >
                                        Locked by {lockedBy}
                                    </Typography>
                                </Box>
                            )}
                    </Grid2>


                    {(forecastType) && (
                        <Grid2 size={{ xs: 12, md: 12, lg: .5 }}>
                            <Box display="flex" justifyContent="flex-end" pr={2}>
                                <IconButton
                                    color="primary"
                                    aria-label="toggle months"
                                    onClick={() => setIsFullscreen((prev) => !prev)}
                                >
                                    {isFullscreen
                                        ? <CloseFullscreenIcon sx={{ color: "#000", fontSize: 30 }} />
                                        : <OpenInFullIcon sx={{ color: "#000", fontSize: 30 }} />
                                    }
                                </IconButton>
                            </Box>

                        </Grid2>
                    )}
                </Grid2>



                {
                    forecastData &&
                    (forecastType === ForecastDisplayType.LiveProposed ||
                        forecastType === ForecastDisplayType.LiveDefinite ||
                        forecastType === ForecastDisplayType.ThirdPartyCost) && (
                        <CustomForecast
                            ref={forecastRef}
                            forecastData={forecastData}
                            startDate={projectData.startDate}// change to the lock date
                            endDate={projectData.endDate}
                            forecastType={forecastType}
                            readOnly={!isLocked}
                        >
                        </CustomForecast>

                    )
                }

                {
                    forecastType === ForecastDisplayType.Historic && historicData && (
                        <ProjectHistoric data={historicData}>

                        </ProjectHistoric>
                    )
                }

                <ProjectInvoice
                    open={modal}
                    onClose={() => setModal(false)}
                    displayType={forecastType!}
                >

                </ProjectInvoice>

            </Box>

            <CustomBackdrop open={loading} />
            <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
        </Box>
    );
};

export default ForecastTracker;